import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';

import { SousCategorie } from 'app/models/sous-categorie';
import { ProduitsService } from 'app/services/produits/produits.service';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

interface PendingImage {
  file: File;
  safeUrl: SafeUrl;      // Sanitized URL for preview (WORKS)
  uploadedUrl?: string;
}

@Component({
  selector: 'app-ajouter-sous-categorie',
  templateUrl: './ajouter-sous-categorie.component.html',
  styleUrls: ['./ajouter-sous-categorie.component.scss']
})
export class AjouterSousCategorieComponent implements OnInit {
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  sousCategorie: SousCategorie = new SousCategorie();
  produit: any;
  idProduit: string | null = null;

  mainImages: PendingImage[] = [];
  secondaryImages: PendingImage[] = [];

  uploadingMain = false;
  uploadingSecondary = false;
  isSubmitting = false;

  constructor(
    private produitService: ProduitsService,
    private sousCategorieService: SousCategorieService,
    private awsService: AwscontrolService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.idProduit = this.activatedRoute.snapshot.paramMap.get('id');
    this.getProduitDetails();
  }

  getProduitDetails(): void {
    if (this.idProduit) {
      this.produitService.getProduit(this.idProduit).subscribe({
        next: (data) => {
          this.produit = data;
          // Ensure categoryList is an array (prevents "diff" error)
          if (this.produit && !Array.isArray(this.produit.categoryList)) {
            this.produit.categoryList = this.produit.categoryList ? [this.produit.categoryList] : [];
          }
        },
        error: () => {
          Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de récupérer le produit', confirmButtonColor: '#f44336' });
        }
      });
    }
  }

  // ---------- File selection & local preview (SANITIZER METHOD - WORKS) ----------
  onMainFilesSelected(event: any): void {
    this.processFiles(event.target.files, 'main');
    event.target.value = '';
  }

  onSecondaryFilesSelected(event: any): void {
    this.processFiles(event.target.files, 'secondary');
    event.target.value = '';
  }

  private processFiles(files: FileList, type: 'main' | 'secondary'): void {
    const filesArray = Array.from(files);
    const validFiles = filesArray.filter(file => file.type.startsWith('image/'));

    if (validFiles.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Aucune image valide', text: 'Formats acceptés: JPEG, PNG, GIF, WebP', timer: 2000 });
      return;
    }

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result;
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(base64);
        const pending: PendingImage = { file, safeUrl };

        if (type === 'main') {
          this.mainImages.push(pending);
        } else {
          this.secondaryImages.push(pending);
        }
        this.cdr.detectChanges(); // Force immediate display
      };
      reader.readAsDataURL(file);
    });
  }

  removeMainImage(index: number): void {
    this.mainImages.splice(index, 1);
    this.cdr.detectChanges();
  }

  removeSecondaryImage(index: number): void {
    this.secondaryImages.splice(index, 1);
    this.cdr.detectChanges();
  }

  // ---------- Upload to AWS ----------
  private async uploadPendingImages(images: PendingImage[]): Promise<string[]> {
    const filesToUpload = images.filter(img => !img.uploadedUrl).map(img => img.file);
    if (filesToUpload.length === 0) {
      return images.map(img => img.uploadedUrl!).filter(url => url);
    }

    return new Promise((resolve, reject) => {
      this.awsService.uploadFiles(filesToUpload, 'sous-categories').subscribe({
        next: (response) => {
          if (response.success && response.urls?.length) {
            let idx = 0;
            for (let i = 0; i < images.length; i++) {
              if (!images[i].uploadedUrl) {
                const rawUrl = response.urls[idx++];
                const fullUrl = rawUrl.startsWith('http') ? rawUrl : this.s3BaseUrl + rawUrl;
                images[i].uploadedUrl = fullUrl;
              }
            }
            resolve(images.map(img => img.uploadedUrl!).filter(url => url));
          } else {
            reject(new Error(response.error || 'Upload failed'));
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  private validateForm(): boolean {
    if (this.uploadingMain || this.uploadingSecondary) {
      Swal.fire({ icon: 'warning', title: 'Upload en cours', text: 'Veuillez patienter', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (this.mainImages.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Image principale requise', text: 'Ajoutez au moins une image principale', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (!this.sousCategorie.name?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Nom requis', text: 'Le nom de la sous-catégorie est obligatoire', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  async onSubmit(): Promise<void> {
    if (!this.validateForm()) return;
    if (!this.idProduit) {
      Swal.fire({ icon: 'error', title: 'ID produit manquant', text: 'Impossible de créer', confirmButtonColor: '#f44336' });
      return;
    }

    this.isSubmitting = true;
    Swal.fire({ title: 'Upload des images...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      this.uploadingMain = true;
      this.uploadingSecondary = true;

      const [mainUrls, secondaryUrls] = await Promise.all([
        this.uploadPendingImages(this.mainImages),
        this.uploadPendingImages(this.secondaryImages)
      ]);

      this.uploadingMain = false;
      this.uploadingSecondary = false;

      const payload = {
        name: this.sousCategorie.name?.trim() || '',
        category: this.sousCategorie.category?.trim() || '',
        website: this.sousCategorie.website?.trim() || '',
        prix: this.sousCategorie.prix || '',
        description: this.sousCategorie.description || '',
        image_url: mainUrls.join(','),
        listimages: secondaryUrls
      };

      Swal.update({ title: 'Création en cours...' });

      this.sousCategorieService.createSousCategorie(payload, this.idProduit).subscribe({
        next: () => {
          this.isSubmitting = false;
          Swal.close();
          Swal.fire({ icon: 'success', title: 'Succès !', text: 'Sous-catégorie créée', timer: 2000, showConfirmButton: false })
            .then(() => this.navigateBack());
        },
        error: (error: HttpErrorResponse) => {
          this.isSubmitting = false;
          Swal.close();
          let msg = 'Erreur lors de la création';
          if (error.error?.message) msg = error.error.message;
          Swal.fire({ icon: 'error', title: 'Erreur', text: msg, confirmButtonColor: '#f44336' });
        }
      });
    } catch (err) {
      this.uploadingMain = false;
      this.uploadingSecondary = false;
      this.isSubmitting = false;
      Swal.close();
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Erreur upload', text: 'Échec du téléchargement des images', confirmButtonColor: '#f44336' });
    }
  }

  navigateBack(): void {
    this.location.back();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser ?',
      text: 'Toutes les données non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.sousCategorie = new SousCategorie();
        this.mainImages = [];
        this.secondaryImages = [];
        this.cdr.detectChanges();
        Swal.fire({ icon: 'success', title: 'Réinitialisé', timer: 1500, showConfirmButton: false });
      }
    });
  }
}